import { CreditCard } from "lucide-react";

import { cx } from "@/components/ui";
import type { PaymentMethod } from "@/lib/types";

export type PayMethod = "card" | "apple_pay" | "google_pay" | "bacs_debit";

/** Each icon is Figma's own SVG at its natural size; Apple's and Google's are drawn upside down there. */
const ICONS: Record<PayMethod, { src: string; width: number; height: number; flip?: boolean }> = {
  card: { src: "/icons/billing/card.svg", width: 14.8333, height: 12.1667 },
  apple_pay: { src: "/icons/billing/apple-pay.svg", width: 12.1667, height: 14.8342, flip: true },
  google_pay: { src: "/icons/billing/google-pay.svg", width: 14.8333, height: 14.8333, flip: true },
  bacs_debit: { src: "/icons/billing/bank-debit.svg", width: 14.8338, height: 14.8333 },
};

/** A payment method's icon in the current text colour (the SVG is used as a mask). */
export function PayIcon({ method, className }: { method: PayMethod; className?: string }) {
  const icon = ICONS[method];
  return (
    <span aria-hidden className={cx("flex size-4 shrink-0 items-center justify-center", className)}>
      <span
        className={cx("block bg-current", icon.flip && "-scale-y-100")}
        style={{
          width: icon.width,
          height: icon.height,
          maskImage: `url(${icon.src})`,
          WebkitMaskImage: `url(${icon.src})`,
          maskSize: "100% 100%",
          WebkitMaskSize: "100% 100%",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
        }}
      />
    </span>
  );
}

/** The icon beside a saved method: Apple Pay's or Google Pay's when it came from a wallet, a bank's for
 * a Direct Debit. */
export function MethodIcon({ method, className }: { method: PaymentMethod; className?: string }) {
  if (method.kind === "bacs_debit")
    return <PayIcon method="bacs_debit" className={cx("size-[18px]", className)} />;
  if (method.wallet) return <PayIcon method={method.wallet} className={cx("size-[18px]", className)} />;
  return <CreditCard className={cx("size-[18px] shrink-0", className)} aria-hidden />;
}
